import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Dinero from 'dinero.js';
import { Field, Formik, FormikProps } from 'formik';
import moment from 'moment-timezone';
import * as yup from 'yup';
import { Grid, Paper, useMediaQuery, useTheme } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import ConfirmationNumberOutlinedIcon from '@material-ui/icons/ConfirmationNumberOutlined';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import WatchLaterOutlinedIcon from '@material-ui/icons/WatchLaterOutlined';
import { Banner, ButtonV2, DiscardButton, Dropdown, Pill, SaveButton, Typography } from '@castiron/components';
import { Coupon } from '@castiron/domain';
import { useTracking } from '@castiron/utils';
import { shopRepository } from '../../../domain';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { getAbandonedCheckoutAutomationAction } from '../../../store/reducers/automations';
import { closeModal, openModal } from '../../../store/reducers/modalConductor';
import { getShopAction } from '../../../store/reducers/shops';
import ButtonGroup from '../../ButtonGroup';
import { LayoutPageProps } from '../../Layout';
import Spinner from '../../Spinner';
import IconWithLines from '../EditWelcomeSeries/IconWithLines';

export type FormValues = {
  status: 'active' | 'inactive';
};

const useStyles = makeStyles((theme: Theme) => ({
  banner: {
    marginBottom: '8px',
  },
  containerGrey: {
    borderRadius: 12,
    background: `${theme.branding.v2.gray[50]}`,
    padding: 16,
  },
  containerWhite: {
    borderRadius: 12,
    border: `1px solid ${theme.branding.v2.gray[200]}`,
    background: `${theme.branding.v2.gray[0]}`,
    padding: 16,
  },
  menuContainer: {
    color: theme.branding.v2.gray[500],
    '&:hover': {
      color: theme.branding.v2.gray[800],
    },
    position: 'absolute',
    top: '8px',
    right: '8px',
    height: '40px',
    width: '40px',
  },
  section: {
    padding: '12px 0px',
  },
}));

const EditAbandonedCheckout: React.FC<LayoutPageProps> = (props: LayoutPageProps) => {
  const { setPageTitle, setBackLocation, setHeaderCTAs, setFooterCTAs } = props;
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { trackEvent } = useTracking();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [pageRefresh, setPageRefresh] = useState<boolean>(false);
  const [coupon, setCoupon] = useState<Coupon | null>(null);
  const [stats, setStats] = useState(null);
  const formikRef = useRef<FormikProps<any>>();

  const { automations, automationsLoading, shop } = useAppSelector(state => ({
    automations: state.automations?.automations,
    automationsLoading: state.automations?.loading,
    shop: state.shops.shop,
  }));

  const mobileMenu = () => (
    <Paper className={classes.menuContainer}>
      <Grid container style={{ height: '100%' }} justify="center" alignItems="center">
        <Grid item>
          <Dropdown
            variant="ellipsis"
            options={[
              {
                label: 'Send Myself Test Emails',
                onClick: () => {
                  dispatch(
                    openModal({
                      modalType: 'AUTOMATION_TEST_MODAL',
                      modalProps: {
                        marketingSendId: 'abandoned-checkout',
                      },
                    }),
                  );
                },
              },
            ]}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          />
        </Grid>
      </Grid>
    </Paper>
  );

  useEffect(() => {
    setPageTitle('Abandoned Checkout');
    setBackLocation('/marketing/automations');
    setHeaderCTAs(
      isMobile
        ? [mobileMenu()]
        : [
            <ButtonV2
              variant="outlined"
              onClick={() =>
                dispatch(
                  openModal({
                    modalType: 'AUTOMATION_TEST_MODAL',
                    modalProps: {
                      marketingSendId: 'abandoned-checkout',
                    },
                  }),
                )
              }
            >
              Send Test Emails
            </ButtonV2>,
          ],
    );
    setFooterCTAs([
      <DiscardButton isSubmitting={isSubmitting} onClick={() => history.push('/marketing/automations')} />,
      <SaveButton
        handleSubmit={() => {
          window.scrollTo(0, 0);
          formikRef?.current?.handleSubmit();
        }}
        isSubmitting={isSubmitting}
      />,
    ]);

    return () => {
      setPageTitle('');
      setBackLocation(false);
    };
  }, [isMobile, isSubmitting]);

  useEffect(() => {
    if (!!shop && !!automations) {
      const { coupon, stats } = automations.abandonedCheckout;

      setCoupon(coupon);
      setStats(stats);
    }
  }, [shop, automations]);

  useEffect(() => {
    if (pageRefresh) {
      dispatch(getAbandonedCheckoutAutomationAction(shop));
    }
  }, [pageRefresh]);

  const onSubmit = async (values: FormValues) => {
    setIsSubmitting(true);

    await Promise.resolve(
      shopRepository.updateProps(shop.id, {
        'config.marketing.abandonedCheckout.enabled': values.status === 'active',
      }),
    );

    await dispatch(getShopAction(shop.id));
    await dispatch(getAbandonedCheckoutAutomationAction(shop));

    trackEvent(`Abandoned Checkout Turned ${values.status === 'active' ? 'On' : 'Off'}`);

    history.push('/marketing/automations');
    setIsSubmitting(false);

    dispatch(
      openModal({
        modalType: 'SIMPLE_ALERT_V2',
        modalProps: {
          show: true,
          title: 'Success!',
          content: 'The Automation has been Updated.',
          buttonText: 'Done',
          handleAction: () => dispatch(closeModal()),
        },
      }),
    );
  };

  const formSchema = yup.object().shape({
    status: yup.string().required('Active value required'),
  });

  const getCouponValue = (coupon: Coupon): string => {
    return `${
      coupon.discount.type === 'amount'
        ? `${Dinero({ amount: coupon.discount.value }).toFormat('$0.00')} Off`
        : `${coupon.discount.value}% Off`
    }`;
  };

  const handleAddCoupon = () => {
    dispatch(
      openModal({
        modalType: 'MANAGE_COUPON_MODAL',
        modalProps: {
          couponId: coupon?.id || '',
          variant: 'abandoned-checkout',
          setPageRefresh: setPageRefresh,
        },
      }),
    );
  };

  const formattedTotalRevenue =
    typeof stats?.totalRevenue === 'number'
      ? Dinero({ amount: stats?.totalRevenue }).toFormat('$0,0.00')
      : stats?.totalRevenue;

  const waitSection = (hour: 1 | 24) => (
    <Grid container xs={12} justify="flex-start" alignItems="center" style={{ margin: '16px 0px' }}>
      <Grid item style={{ marginRight: 5 }}>
        <IconWithLines
          showTopLine
          icon={<WatchLaterOutlinedIcon style={{ color: theme.branding.v2.gray[400] }} />}
          lineHeight="20px"
          offset="-16px"
        />
      </Grid>
      <Grid item>
        <Typography variant="body2">
          Wait {hour} hour{hour === 1 ? '' : 's'}
        </Typography>
      </Grid>
    </Grid>
  );

  return (
    <>
      {automationsLoading || isSubmitting ? (
        <Grid justify="center" container>
          <Spinner show={true} size={'relative'} />
        </Grid>
      ) : (
        <Formik
          validateOnMount
          onSubmit={onSubmit}
          validationSchema={formSchema}
          innerRef={formikRef}
          initialValues={{
            status: shop?.config?.marketing?.abandonedCheckout?.enabled !== false ? 'active' : 'inactive',
          }}
        >
          {({ errors, setFieldValue, touched, values }): ReactElement => (
            <Grid container xs={12} style={{ padding: isMobile ? 16 : 0 }}>
              <Grid container item sm={12} md={7} style={{ padding: isMobile ? 0 : '0px 16px' }}>
                <Typography variant="subtitle1">Automated Emails</Typography>
                <Grid container className={classes.containerGrey}>
                  <Grid container xs={12} justify="flex-start" alignItems="center" style={{ margin: '16px 0px' }}>
                    <Grid item style={{ marginRight: 5 }}>
                      <IconWithLines
                        showTopLine={false}
                        icon={<FiberManualRecordIcon style={{ color: theme.branding.v2.gray[400] }} />}
                        lineHeight="22px"
                        offset="-16px"
                      />
                    </Grid>
                    <Grid item>
                      <Typography variant="body2">Customer abandons checkout</Typography>
                    </Grid>
                  </Grid>
                  {waitSection(1)}
                  <Grid container className={classes.containerWhite} xs={12} justify="flex-start" alignItems="center">
                    <Grid item xs={12}>
                      <Typography variant="caption">EMAIL 1/2</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body1">Nudge customers to finish checking out.</Typography>
                    </Grid>
                  </Grid>
                  {waitSection(24)}
                  <Grid container className={classes.containerWhite} xs={12} justify="flex-start" alignItems="center">
                    <Grid item xs={12}>
                      <Typography variant="caption">EMAIL 2/2</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body1">Last reminder for customers to complete their purchase.</Typography>
                    </Grid>
                    {coupon ? (
                      <>
                        <Grid item xs={12} className={classes.section}>
                          <Grid
                            container
                            justify="center"
                            alignItems="center"
                            style={{
                              border: `2px dashed ${theme.branding.v2.gray[300]}`,
                              color: theme.branding.v2.gray[300],
                              padding: '10px 0px',
                            }}
                          >
                            <ConfirmationNumberOutlinedIcon style={{ marginRight: 5 }} />
                            <Typography style={{ color: theme.branding.v2.gray[800] }}>{`${getCouponValue(
                              coupon,
                            )} Coupon`}</Typography>
                          </Grid>
                        </Grid>
                      </>
                    ) : (
                      <>
                        <Grid item xs={12} className={classes.section}>
                          <Banner variant="info-blue">
                            <Typography variant="body2" style={{ color: theme.branding.v2.blue[500] }}>
                              Studies show 86% of customers are more likely to buy when the marketing includes a special
                              offer.
                            </Typography>
                          </Banner>
                        </Grid>
                        <Grid item xs={12} className={classes.section}>
                          <ButtonV2 variant="contained" fullWidth onClick={handleAddCoupon}>
                            + Add a Coupon
                          </ButtonV2>
                        </Grid>
                      </>
                    )}
                  </Grid>
                  <Grid container xs={12} justify="flex-start" alignItems="center" style={{ margin: '16px 0px' }}>
                    <Grid item style={{ marginRight: 5 }}>
                      <IconWithLines
                        showBottomLine={false}
                        icon={<FiberManualRecordIcon style={{ color: theme.branding.v2.gray[400] }} />}
                        lineHeight="22px"
                        offset="-16px"
                      />
                    </Grid>
                    <Grid item>
                      <Typography variant="body2">End</Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid container item sm={12} md={5} spacing={2} style={{ padding: '0px 16px' }}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">Description</Typography>
                  <Typography variant="body1">
                    Automatically send 2 email reminders to shoppers who left at checkout.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">Audience</Typography>
                  <Typography variant="body1">Everyone</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">Timing</Typography>
                  <Typography variant="body1">Ongoing</Typography>
                  {stats?.lastSent && (
                    <Typography variant="body2" style={{ color: theme.branding.v2.gray[500] }}>
                      {`Last Sent ${moment.unix(stats?.lastSent).format('MMMM D, YYYY')}`}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Grid container justify="space-between">
                    <Typography variant="subtitle1">Coupon</Typography>
                    <ButtonV2 onClick={handleAddCoupon} style={{ padding: 0 }}>
                      {coupon ? 'Manage' : '+ Add'}
                    </ButtonV2>
                  </Grid>
                  {coupon ? (
                    <Pill content={getCouponValue(coupon)} variant="gray" />
                  ) : (
                    <Typography variant="body1">None</Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">Status</Typography>
                  <Field
                    required
                    as={ButtonGroup}
                    error={touched.status && errors.status}
                    name="status"
                    value={values.status}
                    onChange={(value): void => {
                      switch (value) {
                        case 'active':
                          setFieldValue('status', 'active');
                          break;
                        case 'inactive':
                          setFieldValue('status', 'inactive');
                          break;
                      }
                    }}
                    buttons={['active', 'inactive']}
                  />
                  {values.status === 'inactive' && (
                    <Banner variant="info-blue" className={classes.banner}>
                      <Typography variant="body2" style={{ color: theme.branding.v2.blue[500] }}>
                        {stats?.totalRevenue
                          ? `You have earned ${formattedTotalRevenue} from this automation—keep it running to maximize your sales!`
                          : 'This automation is working behind the scenes to boost your sales. Keep it on to maximize your earnings!'}
                      </Typography>
                    </Banner>
                  )}
                  <Typography variant="body2">
                    We recommend you keep this automation on. To customize this experience, manage the associated coupon
                    in Email 2 or turn off the automation.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">Activity</Typography>
                  <Grid container>
                    <Grid item xs={6}>
                      <Typography variant="body2">Sent</Typography>
                      <Typography variant="body1">{stats?.totalSends || 0}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">Revenue</Typography>
                      <Typography variant="body1">{formattedTotalRevenue}</Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Formik>
      )}
    </>
  );
};

export default EditAbandonedCheckout;
